import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  CommissionType,
  addCommissionByPerPerson,
  addCommissionByPercent,
  agentCommissionPerPerson,
  agentCommissionPercent,
} from '../../ShoppingCartSliceV2';
import { Input } from 'antd';
import './agentActionProfit.less';
import { debounce } from 'lodash';
import revealOnKeyboard from '../../../../utils/revealOnKeyboard';

interface Props {
  isMangeOrderPage: boolean;
}

const MIN_PROFIT_PERCENTAGE = 0;
const MAX_PROFIT_PERCENTAGE = 99;

const clampProfitPercentage = (value: number) =>
  Math.min(Math.max(value, MIN_PROFIT_PERCENTAGE), MAX_PROFIT_PERCENTAGE);

const isProfitPercentageOutOfRange = (value: number) =>
  isNaN(value) || value < MIN_PROFIT_PERCENTAGE || value > MAX_PROFIT_PERCENTAGE;

const AgentActionProfit = ({ isMangeOrderPage }: Props) => {
  const { shoppingCartTranslations: t, general: general_t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const { agent_commission, ShoppingCartV2 } = useSelector(
    (data: RootState) => data.shoppingCartV2
  );

  const dispatch = useDispatch();

  const [perPersonValue, setPerPersonValue] = useState(
    agent_commission?.per_pax ?? 0
  );
  const [perPercentageValue, setPerPercentageValue] = useState(
    clampProfitPercentage(agent_commission?.percentage ?? 5)
  );
  const [percentageError, setPercentageError] = useState(false);

  const handleCommissionPerPersonChange = debounce((value: number) => {

    const nonEmptyValue = value ?? 0;
    if (!isNaN(Number(nonEmptyValue))) {
      dispatch(agentCommissionPerPerson(Number(nonEmptyValue)));
      setTimeout(() => {
        dispatch(addCommissionByPerPerson());
      }, 150);
    }
  }, 300); // 300

  const handleCommissionPerPercentageChange = debounce((value: number) => {
    const clampedValue = clampProfitPercentage(value ?? 0);
    if (!isNaN(clampedValue)) {
      dispatch(agentCommissionPercent(clampedValue));
      setTimeout(() => {
        dispatch(addCommissionByPercent());
      }, 150);
    }
  }, 300);

  const handleChange = (val: number, type: 'perPerson' | 'perPercentage') => {
    if (type === 'perPerson') {
      setPerPersonValue(val);
      handleCommissionPerPersonChange(val);
    } else {
      setPerPercentageValue(val);
      const invalid = isProfitPercentageOutOfRange(val);
      setPercentageError(invalid);
      if (!invalid) {
        handleCommissionPerPercentageChange(val);
      }
    }
  };

  const handlePercentageBlur = () => {
    if (!isProfitPercentageOutOfRange(perPercentageValue)) {
      return;
    }

    const clampedValue = clampProfitPercentage(perPercentageValue);
    setPerPercentageValue(clampedValue);
    setPercentageError(false);
    handleCommissionPerPercentageChange(clampedValue);
  };

  return (
    <div className="agent-action-profit">
      <div
        id="obs-cart-profit-pct"
        className={`input-wrapper ${
          agent_commission?.type !== CommissionType.Percentage
            ? 'input-wrapper-disabled'
            : ''
        }`}
        onClick={(e) => {
          if (
            agent_commission?.type === CommissionType.PerPax &&
            !isProfitPercentageOutOfRange(perPercentageValue)
          ) {
            handleCommissionPerPercentageChange(perPercentageValue);
          }
        }}
      >
        <p>{t.grossProfitPercentage}</p>
        <div>
          <Input
            type="number"
            min={MIN_PROFIT_PERCENTAGE}
            max={MAX_PROFIT_PERCENTAGE}
            className="agent--action-input"
            addonAfter={<div>%</div>}
            status={percentageError ? 'error' : undefined}
            onChange={(e) => handleChange(+e.target.value, 'perPercentage')}
            onBlur={handlePercentageBlur}
            // phones: the whole profit block stays in view as the keyboard opens
            onFocus={(e) =>
              revealOnKeyboard(e.currentTarget.closest('.agent-action-profit'))
            }
            value={perPercentageValue}
          />
          {percentageError && (
            <span className="percentage-error">
              {t.grossProfitPercentageInvalidRange}
            </span>
          )}
        </div>
      </div>
      <span>{t.or}</span>
      <div
        id="obs-cart-profit-perpax"
        className={`input-wrapper ${
          agent_commission?.type !== CommissionType.PerPax
            ? 'input-wrapper-disabled'
            : ''
        }`}
        onClick={() => {
          if (agent_commission?.type === CommissionType.Percentage)
            handleCommissionPerPersonChange(perPersonValue);
        }}
      >
        <p>{t.profitPerPerson}</p>
        <Input
          type="number"
          className="agent--action-input"
          addonAfter={<div>{ShoppingCartV2.currency_sign}</div>}
          // onChange={(e) => handleCommissionPerPersonChange(e.target.value)}
          onChange={(e) => handleChange(+e.target.value, 'perPerson')}
          onFocus={(e) =>
            revealOnKeyboard(e.currentTarget.closest('.agent-action-profit'))
          }
          value={perPersonValue}
        />
      </div>
    </div>
  );
};

export default AgentActionProfit;
